import log from "loglevel";
import { inject, injectable } from "tsyringe";
import { TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";

export const TOKEN_IDomConnectivity = Symbol('IDomConnectivity');

/**
 * Monitors browser online/offline status and dispatches events to ProjectBus
 */
export interface IDomConnectivity {
    /**
     * Returns the current online status of the browser
     */
    get isOnline(): boolean;

    /**
     * Manually checks the online status
     * @returns true if online, false otherwise
     */
    checkOnlineStatus(): boolean;
}

@injectable()
export class DomConnectivity implements IDomConnectivity {
    private _isOnline: boolean;

    constructor(
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus
    ) {
        // Initialize with current online status
        this._isOnline = navigator.onLine;

        // Subscribe to online/offline events
        window.addEventListener('online', this.handleOnlineStatusChange);
        window.addEventListener('offline', this.handleOnlineStatusChange);

        log.info("DomConnectivity initialized, current online status:", this._isOnline);
    }

    get isOnline(): boolean {
        return this._isOnline;
    }

    checkOnlineStatus(): boolean {
        // This gets the current navigator.onLine value
        const currentStatus = navigator.onLine;

        // If the status has changed since our last check, update and emit event
        if (currentStatus !== this._isOnline) {
            this._isOnline = currentStatus;
            this.publishStatusChange();
        }

        return this._isOnline;
    }

    private handleOnlineStatusChange = () => {
        const wasOnline = this._isOnline;
        this._isOnline = navigator.onLine;

        // Only publish if there was a real change
        if (wasOnline !== this._isOnline) {
            log.info(`Network connectivity changed: ${this._isOnline ? 'online' : 'offline'}`);
            this.publishStatusChange();
        }
    };

    private publishStatusChange(): void {
        this.projectBus.connectivityChanged(this._isOnline);
    }
}